import { ChangeDetectionStrategy, Component, computed, CUSTOM_ELEMENTS_SCHEMA, effect, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControlStatus, ReactiveFormsModule, Validators } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, startWith } from 'rxjs/operators';
import { HandoverLog } from '../../../../models/handover-log.model';
import { FormStatus, HandoverTaskFormValue } from '../task.dialog.model';
import { TaskFormDialogBase } from '../task.form-base.dialog';
import { AppDropdownComponent, AppDropdownOption } from '../../../../shared/components/app-dropdown/app-dropdown.component';
import { ACTIVITY_TYPES_HANDOVER_DATA } from '../activity-types.data';
import { NO_PROGRAM_LABEL } from '../../../../shared/constants/program';

@Component({
  standalone: true,
  selector: 'dr-ops-stream-ui-handover-task-dialog',
  imports: [CommonModule, ReactiveFormsModule, AppDropdownComponent],
  templateUrl: './handover-task.dialog.html',
  styleUrls: ['./../task.dialog.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HandoverTaskDialog extends TaskFormDialogBase<HandoverLog, HandoverTaskFormValue> {
  readonly activityTypes = signal<string[]>(ACTIVITY_TYPES_HANDOVER_DATA);

  readonly activityOptions = computed<AppDropdownOption[]>(() =>
    this.activityTypes().map((value, index) => ({
      value: value,
      label: value,
    }))
  );

  readonly programOptionsWithOther = computed<AppDropdownOption[]>(() => [
    ...this.programOptions(),
    { value: NO_PROGRAM_LABEL, label: 'Other' },
  ]);

  // -----------------------
  // Form
  // -----------------------
  readonly form = this.fb.group({
    programId: this.fb.control<string>(NO_PROGRAM_LABEL, { nonNullable: true }),
    notes: this.fb.control<string>('', { nonNullable: true }),
    dueDate: this.fb.control<string | null>(null, { validators: [Validators.required] }),
    timezone: this.fb.control<string>('UTC', { nonNullable: true }),
    activity: this.fb.control<string>('', { nonNullable: true, validators: [Validators.required] }),
  });

  readonly formValid = toSignal(
    this.form.statusChanges.pipe(
      startWith(this.form.status),
      map((s: FormControlStatus) => s === FormStatus.VALID)
    ),
    { initialValue: this.form.valid }
  );

  readonly canSubmit = computed(() => !this.isViewOnly() && this.formValid());
  readonly confirmLabel = computed(() => (this.isEdit() ? 'Save' : 'Confirm'));

  constructor() {
    super();
    /**
     * Initialize the form from data
     */
    effect(() => {
      const defaults = this.data.defaults ?? {};

      const initial: HandoverTaskFormValue = {
        programId: NO_PROGRAM_LABEL,
        notes: '',
        dueDate: '',
        timezone: 'UTC',
        activity: '',
        ...defaults,
      };

      this.form.reset(initial);

      if (this.isViewOnly()) {
        this.form.disable({ emitEvent: false });
      } else {
        this.form.enable({ emitEvent: false });
      }

      // The dialog reads and writes a UTC wall-clock: the timezone is pinned and never editable.
      this.form.controls.timezone.setValue('UTC', { emitEvent: false });
      this.form.controls.timezone.disable({ emitEvent: false });
    });
  }
}
